import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import posthog from 'posthog-js';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  User,
  Users,
  Search,
  Briefcase,
  FileText,
  TrendingUp,
  MapPin,
  DollarSign,
  Clock,
  Share2,
  Sparkles,
  BookOpen,
  MessageSquare,
  LogOut,
  Settings,
  Bell,
  Bookmark,
  Heart,
  Navigation,
  Download,
  Building,
  Star,
  CheckCircle2,
  Calendar,
  Compass,
  Lightbulb,
  Target
} from 'lucide-react';
import { Progress } from '../ui/progress';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { RoleBadge } from '../common/RoleBadge';
import { useApplicationsQuery, useSavedJobsQuery, useJobMatchesQuery, useMatchFeedback } from '../../hooks/useCandidateData';
import { CandidateJobsTab } from './CandidateJobsTab';
import type { JobMatch } from '../../api/jobMatches';
import type { Job } from '../../api/jobs';
import { useCandidateDashboardSummary } from '../../hooks/useCandidateDashboardSummary';
import { useSavedSearches } from '../../hooks/useSavedSearches';
import type {
  CandidateInsight,
  CandidateTrainingRecommendation,
  CandidateAnnouncement,
} from '../../api/candidateDashboard';
import { toast } from 'sonner';
import { usePracticeHub } from '../../hooks/usePracticeHub';
import { useAuth } from '../../hooks/useAuth';
import type { CandidateApplication } from '../../api/applications';
import type { SavedJob } from '../../api/savedJobs';
import { isEmployerVerified } from '../../lib/jobVerification';
import { useCandidateProfileScore, useCandidateProfileSection, useCandidateTeamSnapshot } from '../../hooks/useCandidateProfile';
import { useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';

type DashboardJob = JobMatch | SavedJob | Job;

interface CandidateDashboardProps {
  teamMode?: boolean;
}

export function CandidateDashboard({ teamMode = false }: CandidateDashboardProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedJob, setSelectedJob] = useState<DashboardJob | null>(null);
  const [isClient, setIsClient] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) return;
    const prefixes =
      activeTab === 'overview'
        ? ['candidate-dashboard-summary', 'candidate-job-matches', 'candidate-applications', 'candidate-saved-jobs', 'candidate-profile-score']
        : activeTab === 'jobs'
        ? ['candidate-job-matches', 'candidate-saved-searches', 'candidate-saved-jobs']
        : activeTab === 'applications'
        ? ['candidate-applications']
        : activeTab === 'career'
        ? ['candidate-dashboard-summary', 'career-path']
        : activeTab === 'team'
        ? ['candidate-profile']
        : ['candidate-dashboard-summary'];
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey?.[0];
        return typeof key === 'string' && prefixes.some((p) => key === p);
      },
    });
  }, [activeTab, isClient, queryClient]);

  const {
    data: savedJobs = [],
    isPending: savedJobsPending,
  } = useSavedJobsQuery();
  const {
    data: jobMatches = [],
    isPending: jobMatchesPending,
    isError: jobMatchesError,
  } = useJobMatchesQuery();
  const matchFeedbackMutation = useMatchFeedback();
  const dashboardSummaryQuery = useCandidateDashboardSummary();
  const dashboardSummary = dashboardSummaryQuery.data;
  const dashboardPending = dashboardSummaryQuery.isPending;
  const dashboardError = dashboardSummaryQuery.isError;
  const {
    savedSearches,
    defaultCadence,
    savedSearchesLoading,
    savedSearchesError,
  } = useSavedSearches();
  const practiceHubQuery = usePracticeHub();
  const practiceHubData = practiceHubQuery.data;
  const { logout } = useAuth();
  const profileScoreQuery = useCandidateProfileScore();
  const profileScore = profileScoreQuery.data?.score;
  const profileLevel = profileScoreQuery.data?.level;
  const teamSnapshotQuery = useCandidateTeamSnapshot();
  const teamSnapshot = teamSnapshotQuery.data;
  const isTeamCandidate = Boolean(teamSnapshot?.isTeamCandidate);
  const teamMembersQuery = useCandidateProfileSection('work-experience', teamMode && isTeamCandidate);
  const teamMembers = (teamMembersQuery.data ?? []).filter(
    (entry) => `${entry.teamMember ?? ''}`.toLowerCase() === 'true',
  );
  const listedTeamMembers =
    teamMembers.length > 0
      ? teamMembers
      : (teamSnapshot?.teamMemberNames ?? []).map((name) => ({ name }) as Record<string, string>);

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  // Get jobs data with company info
  const {
    data: applicationsData = [],
    isPending: applicationsPending,
  } = useApplicationsQuery();
  const applications = applicationsData as CandidateApplication[];

  const careerPath = [
    { role: 'Care Assistant', current: true, completed: true },
    { role: 'Senior Care Assistant', current: false, completed: false, recommended: true },
    { role: 'Care Team Leader', current: false, completed: false },
    { role: 'Care Manager', current: false, completed: false },
  ];

const FALLBACK_TRAINING = [
  { id: 'training-1', title: 'Advanced First Aid Certification', provider: 'St John Ambulance', duration: '2 days', price: 'Free' },
  { id: 'training-2', title: 'Dementia Care Specialist', provider: 'Care Training UK', duration: '1 week', price: '£299' },
  { id: 'training-3', title: 'Team Leadership in Healthcare', provider: 'NHS Academy', duration: '3 weeks', price: 'Free' },
];

const FALLBACK_INSIGHTS = [
  {
    id: 'match-momentum',
    title: 'Match momentum',
    value: '+22%',
    description: 'Keyword updates and skills refresh lifted match quality in the past 7 days.',
    highlights: ['4 new curated roles', 'Top match: Senior Care Assistant'],
    icon: 'Sparkles',
  },
  {
    id: 'profile-impact',
    title: 'Profile impact',
    value: '92%',
    description: 'Profile strength is outperforming similar candidates—keep showcasing achievements.',
    highlights: ['Availability set to immediate', 'Add care awards to unlock +5%'],
    icon: 'Lightbulb',
  },
  {
    id: 'career-navigator',
    title: 'Career navigator',
    value: '3 steps',
    description: 'You are three milestones away from your targeted Care Team Leader role.',
    highlights: ['Schedule leadership workshop', 'Request mentor session'],
    icon: 'Compass',
  },
];

  const recommendedMatches = jobMatches.slice(0, 2);
  const fallbackRecommended = savedJobs.slice(0, 2);
  const highlightMatches = recommendedMatches.length ? recommendedMatches : fallbackRecommended;
  const insights = dashboardSummary?.insights ?? FALLBACK_INSIGHTS;
  const trainingRecommendations = dashboardSummary?.training ?? FALLBACK_TRAINING;
  const announcements = dashboardSummary?.announcements ?? [];
  const insightsLoading = dashboardPending && !dashboardSummary?.insights;
  const announcementsLoading = dashboardPending && !dashboardSummary?.announcements;
  const cadenceLabelMap: Record<string, string> = {
    daily: 'Daily digest',
    weekly: 'Weekly summary',
    off: 'Notifications muted',
  };
  const notificationCadenceLabel = cadenceLabelMap[defaultCadence] ?? 'Weekly summary';
  const activeSavedAlerts = savedSearches.filter((search) => search.cadence !== 'off').length;

const insightLinkMap: Record<string, string> = {
  'match-momentum': '/candidate/job-matches',
  'profile-impact': '/candidate/profile',
  'career-navigator': '/candidate/path',
};

const resolveInsightLink = (insight: CandidateInsight) =>
  (insight as { ctaLink?: string }).ctaLink ?? insightLinkMap[insight.id] ?? '/candidate/dashboard';

  const handleInsightClick = (insight: CandidateInsight) => {
    const destination = resolveInsightLink(insight);
    posthog.capture('candidate_dashboard_insight_click', {
      insightId: insight.id,
      title: insight.title,
      destination,
    });
  };

const handleTrainingClick = (training: CandidateTrainingRecommendation) => {
  posthog.capture('candidate_dashboard_training_click', {
    trainingId: training.id,
    provider: training.provider,
    price: training.price,
  });
  if (training.link && typeof window !== 'undefined') {
    window.open(training.link, '_blank', 'noopener,noreferrer');
  }
};

const handleOpenNotifications = () => {
  posthog.capture('candidate_notifications_card_click', {
    savedSearches: savedSearches.length,
    cadence: defaultCadence,
  });
};
const handleMatchFeedback = (matchId: string | number, feedback: 'positive' | 'negative') => {
  matchFeedbackMutation.mutate(
    { matchId, feedback },
    {
      onSuccess: () => {
        toast.success(
          feedback === 'positive'
            ? 'Great! We will surface similar matches.'
            : 'Thanks, we will refine future matches.',
        );
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : 'Unable to send feedback.';
        toast.error(message);
      },
    },
  );
};
const announcementLinkMap: Record<string, string> = {
  'announcement-1': '/candidate/practice',
};

const severityLinkMap: Record<NonNullable<CandidateAnnouncement['severity']>, string> = {
  info: '/candidate/dashboard',
  success: '/candidate/dashboard',
  warning: '/candidate/applications',
  error: '/candidate/settings',
};

const resolveAnnouncementLink = (announcement: CandidateAnnouncement) =>
  announcement.ctaLink ??
  announcementLinkMap[announcement.id] ??
  (announcement.severity ? severityLinkMap[announcement.severity] : undefined) ??
  '/candidate/dashboard';

const resolveAnnouncementLabel = (announcement: CandidateAnnouncement) => {
  if (announcement.ctaLabel) return announcement.ctaLabel;
  return announcement.severity === 'warning' || announcement.severity === 'error' ? 'Take action' : 'View details';
};

const handleAnnouncementClick = (announcement: CandidateAnnouncement, destination: string) => {
  posthog.capture('candidate_dashboard_announcement_click', {
    announcementId: announcement.id,
    severity: announcement.severity,
    destination,
  });
};

  const savedJobsValue = savedJobsPending ? '...' : savedJobs.length.toString();
  const savedJobsTrend = savedJobsPending ? 'Syncing' : savedJobs.length ? 'Review your shortlist' : 'Save roles to compare';
  const matchesValue = jobMatchesPending ? '...' : jobMatches.length.toString();
  const matchesTrend = jobMatchesPending ? 'Scanning preferences' : 'Updated today';
  const applicationsValue = applicationsPending ? '...' : applications.length.toString();
  const applicationsTrend = applicationsPending
    ? 'Syncing'
    : applications.length
      ? 'Track interviews'
      : 'Apply to roles';

  const getStatusBadgeClass = (statusType: string) => {
    switch (statusType) {
      case 'interview':
        return 'bg-green-500/20 text-green-200 border-green-500/40';
      case 'shortlisted':
        return 'bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/40';
      case 'rejected':
        return 'bg-red-500/20 text-red-300 border-red-500/40';
      default:
        return 'bg-blue-500/20 text-blue-300 border-blue-500/40';
    }
  };

  const overviewStats = [
    {
      label: 'Profile Strength',
      value: profileScore !== undefined ? `${profileScore}%` : '—',
      icon: User,
      trend: profileLevel ? `Level: ${profileLevel}` : 'Complete your profile',
      link: '/candidate/profile-strength',
    },
    { label: 'Applications', value: applicationsValue, icon: FileText, trend: applicationsTrend, link: '/candidate/applications' },
    {
      label: 'Saved Jobs',
      value: savedJobsValue,
      icon: Bookmark,
      trend: savedJobsTrend,
      link: '/candidate/saved-jobs',
    },
    {
      label: 'Job Matches',
      value: matchesValue,
      icon: Sparkles,
      trend: matchesTrend,
      link: '/candidate/job-matches',
    },
  ];

const resolveSelectedCompany = (job: DashboardJob | null) => {
  if (!job) return 'Unknown Company';
  if ('company' in job && job.company) {
    return job.company as string;
  }
  if ('employer' in job) {
    const employer = (job as { employer?: { name?: string } }).employer;
    if (employer?.name) return employer.name;
  }
  return 'Unknown Company';
};

  if (!teamMode && teamSnapshotQuery.isSuccess && isTeamCandidate) {
    return <Navigate to="/candidate/team-dashboard" replace />;
  }

  return (
    <main
      className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]"
      aria-labelledby="candidate-dashboard-title"
    >
      <Helmet>
        <title>Candidate Dashboard | Lighthouse Jobs</title>
        <meta
          name="description"
          content="Review matches, applications, training, and notifications tailored to your Lighthouse Jobs profile."
        />
      </Helmet>
      {/* Header */}
      <header className="border-b border-white/10 bg-[rgba(5,9,19,0.82)] backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gold-gradient flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-black" />
              </div>
              <span className="text-gold-gradient">
                Lighthouse Jobs
              </span>
            </Link>
            
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" className="relative text-white">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#ba9036] rounded-full" />
              </Button>
              <Link to="/life">
                <Button variant="outline" className="border-[#d2ac47]/50 text-[#ba9036] hover:bg-[#ba9036]/10">
                  Life Platform
                </Button>
              </Link>
              <Link to="/candidate/settings">
                <Button variant="ghost" size="icon" className="text-white">
                  <Settings className="w-5 h-5" />
                </Button>
              </Link>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-white"
                onClick={handleLogout}
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 space-y-2">
          <RoleBadge role="candidate" />
          <h1 id="candidate-dashboard-title" className="mb-2 text-gold-gradient">
            {teamMode ? 'Team Candidate Dashboard' : 'Candidate Dashboard'}
          </h1>
          <p className="text-gray-400">
            {teamMode
              ? 'Manage your team profile and keep applications, matches, and progress in one place.'
              : 'Find your perfect career opportunity'}
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} aria-label="Candidate dashboard sections">
          <TabsList className="bg-white/5 border border-white/10 mb-8">
            <TabsTrigger value="overview" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              <TrendingUp className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="jobs" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              <Search className="w-4 h-4 mr-2" />
              Find Jobs
            </TabsTrigger>
            <TabsTrigger value="applications" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              <FileText className="w-4 h-4 mr-2" />
              Applications
            </TabsTrigger>
            <TabsTrigger value="career" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              <BookOpen className="w-4 h-4 mr-2" />
              Career Path
            </TabsTrigger>
            {teamMode && (
              <TabsTrigger value="team" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
                <Users className="w-4 h-4 mr-2" />
                Team Hub
              </TabsTrigger>
            )}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {overviewStats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <Link key={index} to={stat.link}>
                    <Card
                      className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300 cursor-pointer"
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4 bg-[rgba(169,160,160,0)]">
                          <div className="w-12 h-12 rounded-lg bg-gold-gradient flex items-center justify-center">
                            <Icon className="w-6 h-6 text-black" />
                          </div>
                        </div>
                        <div className="text-white mb-1">{stat.value}</div>
                        <p className="text-sm text-gray-400 mb-2">{stat.label}</p>
                        <p className="text-xs text-[#ba9036]">{stat.trend}</p>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>

            {(announcementsLoading || announcements.length > 0) && (
              <Card className="role-surface-candidate border-white/10 bg-white/[0.03]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg">Updates & reminders</CardTitle>
                  <CardDescription className="text-gray-400">
                    Actionable nudges from Lighthouse to keep your search moving.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {announcementsLoading ? (
                    <p className="text-sm text-gray-400">Checking for new announcements…</p>
                  ) : announcements.length === 0 ? (
                    <p className="text-sm text-gray-400">You’re all caught up. We’ll notify you when something changes.</p>
                  ) : (
                    announcements.map((announcement) => {
                      const destination = resolveAnnouncementLink(announcement);
                      const label = resolveAnnouncementLabel(announcement);
                      return (
                        <div
                          key={announcement.id}
                          className="rounded-xl border border-white/10 bg-white/[0.04] p-4 text-sm text-gray-200"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-semibold text-white">{announcement.title}</p>
                              <p className="text-gray-400">{announcement.body}</p>
                            </div>
                            {announcement.severity === 'warning' && (
                              <Badge className="bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50">Action</Badge>
                            )}
                          </div>
                          <Button
                            asChild
                            size="sm"
                            variant="outline"
                            className="mt-3 border-white/20 text-white hover:bg-white/5"
                            onClick={() => handleAnnouncementClick(announcement, destination)}
                          >
                            <Link to={destination}>{label}</Link>
                          </Button>
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            )}

            <Card className="role-surface-candidate border-white/10 bg-white/[0.03]">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg">Notification Centre</CardTitle>
                <CardDescription className="text-gray-400">
                  Control digests, SMS alerts, and saved search updates.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {savedSearchesLoading ? (
                  <p className="text-sm text-gray-400">Loading your notification settings…</p>
                ) : savedSearchesError ? (
                  <p className="text-sm text-red-300">
                    We couldn&apos;t load your alert preferences. {savedSearchesError.message || 'Open the notification centre to review them.'}
                  </p>
                ) : (
                  <>
                    <div className="flex items-center justify-between text-sm text-gray-300">
                      <span>Active alerts</span>
                      <span className="text-white font-semibold">{activeSavedAlerts}</span>
                    </div>
                      <div className="flex items-center justify-between text-sm text-gray-300">
                        <span>Saved searches</span>
                        <span className="text-white font-semibold">{savedSearches.length}</span>
                      </div>
                    <div className="flex items-center justify-between text-sm text-gray-300">
                      <span>Default cadence</span>
                      <span className="text-white font-semibold">{notificationCadenceLabel}</span>
                    </div>
                  </>
                )}
                <Button
                  asChild
                  className="bg-gold-gradient text-black hover:brightness-110"
                  onClick={handleOpenNotifications}
                >
                  <Link to="/notifications">Open Notification Centre</Link>
                </Button>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {insightsLoading ? (
                <Card className="role-surface-candidate bg-white/5 border-white/10">
                  <CardContent className="p-6 text-sm text-gray-300">Pulling the latest insights…</CardContent>
                </Card>
              ) : (
                insights.map((insight) => {
                  const IconComponent =
                    insight.icon === 'Lightbulb' ? Lightbulb : insight.icon === 'Compass' ? Compass : Sparkles;
                  return (
                    <Card
                      key={insight.id}
                      className="role-surface-candidate bg-white/5 border-white/10 overflow-hidden"
                    >
                      <CardContent className="space-y-4 p-6">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-xs uppercase tracking-[0.3em] text-blue-200/70">Insight</p>
                            <CardTitle className="mt-1 text-white">{insight.title}</CardTitle>
                          </div>
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 via-indigo-500 to-sky-500 text-white">
                            <IconComponent className="h-5 w-5" aria-hidden />
                          </span>
                        </div>
                        <div>
                          <p className="text-3xl font-semibold text-gold-gradient">{insight.value}</p>
                          <p className="mt-2 text-sm text-gray-300">{insight.description}</p>
                        </div>
                        <ul className="space-y-2 text-sm text-gray-400">
                          {insight.highlights.map((item, highlightIndex) => (
                            <li key={highlightIndex} className="flex items-center gap-2">
                              <span className="inline-flex h-1.5 w-1.5 rounded-full bg-sky-400" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                        <Button
                          asChild
                          variant="outline"
                          className="border-white/20 text-white hover:bg-white/5"
                          onClick={() => handleInsightClick(insight)}
                        >
                          <Link to={resolveInsightLink(insight)}>Review action</Link>
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="role-surface-candidate bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#ba9036]" />
                    Recommended Jobs
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    AI-matched positions based on your profile
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {jobMatchesPending ? (
                    <p className="text-sm text-gray-400">Scanning for fresh matches...</p>
                  ) : jobMatchesError ? (
                    <p className="text-sm text-red-300">Unable to load matches. Please refresh.</p>
                  ) : recommendedMatches.length === 0 ? (
                    highlightMatches.map((job) => {
                      const verified = isEmployerVerified(job);
                      return (
                        <div
                          key={`fallback-reco-${job.id}`}
                          className="p-4 bg-white/5 rounded-lg border border-white/10 hover:border-[#d2ac47]/50 transition-all cursor-pointer"
                          onClick={() => setActiveTab('jobs')}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="text-white">{job.title}</h4>
                                {verified && <CheckCircle2 className="w-4 h-4 text-[#ba9036]" />}
                              </div>
                              <p className="text-sm text-gray-400">{job.company}</p>
                            </div>
                            <Badge className="bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50">
                              {job.matchScore}% Match
                            </Badge>
                          </div>
                          <div className="flex gap-3 text-xs text-gray-400">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {job.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              {job.salary}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    recommendedMatches.map((match) => {
                      const verified = isEmployerVerified(match);
                      return (
                        <div
                          key={`match-${match.id}`}
                          className="p-4 bg-white/5 rounded-lg border border-white/10 hover:border-[#d2ac47]/50 transition-all cursor-pointer"
                          onClick={() => setSelectedJob(match)}
                        >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="text-white">{match.title}</h4>
                              {verified && <CheckCircle2 className="w-4 h-4 text-[#ba9036]" />}
                            </div>
                            <p className="text-sm text-gray-400">{match.company}</p>
                          </div>
                          <Badge className="bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50">
                            {match.matchScore}% Match
                          </Badge>
                        </div>
                        <div className="flex gap-3 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {match.location}
                          </span>
                          {match.salary && (
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              {match.salary}
                            </span>
                          )}
                        </div>
                        <div className="mt-3 flex gap-2 text-xs">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-300 hover:text-green-100"
                            disabled={matchFeedbackMutation.isPending}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleMatchFeedback(match.id, 'positive');
                            }}
                          >
                            Keep similar
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-300 hover:text-red-100"
                            disabled={matchFeedbackMutation.isPending}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleMatchFeedback(match.id, 'negative');
                            }}
                          >
                            Not relevant
                          </Button>
                        </div>
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>

              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-[#ba9036]" />
                    Recommended Training
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Boost your skills with these courses
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {dashboardPending && !dashboardSummary?.training ? (
                    <p className="text-sm text-gray-400">Loading recommended training…</p>
                  ) : trainingRecommendations.length === 0 ? (
                    <p className="text-sm text-gray-400">No recommendations yet. Update your profile interests.</p>
                  ) : (
                    trainingRecommendations.slice(0, 2).map((training) => (
                      <div
                        key={training.id}
                        className="p-3 bg-white/5 rounded-lg border border-white/10"
                      >
                        <h4 className="text-white text-sm mb-1">{training.title}</h4>
                        <p className="text-xs text-gray-400 mb-2">{training.provider}</p>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-400">{training.duration}</span>
                          <Badge variant="outline" className="border-[#d2ac47]/50 text-[#ba9036]">
                            {training.price}
                          </Badge>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-3 border-white/20 text-white hover:bg-white/5"
                          onClick={() => handleTrainingClick(training)}
                        >
                          View details
                        </Button>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>

            <Card className="role-surface-candidate bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#ba9036]" />
                  Interview Preparation Assistant
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Practice with AI, review tips, and explore calculators in the hub.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {practiceHubQuery.isPending || !practiceHubData ? (
                  <p className="text-sm text-gray-400">Loading practice hub summary…</p>
                ) : (
                  <>
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <p className="text-xs uppercase tracking-wide text-gray-400">Featured prompts</p>
                      <div className="mt-3 space-y-2">
                        {practiceHubData.practicePrompts.slice(0, 2).map((prompt) => (
                          <div key={prompt.id} className="flex items-center justify-between text-sm text-gray-200">
                            <div>
                              <p className="text-white">{prompt.title}</p>
                              <p className="text-xs text-gray-400">{prompt.focus}</p>
                            </div>
                            <Badge className="bg-white/10 text-xs capitalize text-gray-200">{prompt.difficulty}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <p className="text-xs uppercase tracking-wide text-gray-400">Recent progress</p>
                      <div className="mt-3 space-y-2 text-sm text-gray-300">
                        {practiceHubData.recentSessions.slice(0, 2).map((session) => (
                          <div key={session.id} className="flex items-center justify-between">
                            <span>{session.role}</span>
                            <Badge
                              variant="outline"
                              className={
                                session.status === 'completed'
                                  ? 'border-green-400/50 text-green-300'
                                  : 'border-[#d2ac47]/50 text-[#ba9036]'
                              }
                            >
                              {session.status === 'completed' ? 'Completed' : 'In progress'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-gray-300">
                      <p className="text-xs uppercase tracking-wide text-gray-400">AI tips</p>
                      <ul className="mt-2 space-y-1">
                        {practiceHubData.aiCoachTips.slice(0, 2).map((tip, idx) => (
                          <li key={idx} className="flex gap-2">
                            <span className="text-[#ba9036]">•</span>
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}
                <Button asChild className="w-full bg-gold-gradient text-black hover:brightness-110">
                  <Link to="/candidate/practice">Open practice hub</Link>
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs" className="space-y-8">
            <CandidateJobsTab onSelectJob={(job) => setSelectedJob(job)} savedJobs={savedJobs} />
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications" className="space-y-8">
            <div className="mx-auto w-full max-w-5xl space-y-6">
              <header className="rounded-2xl border border-white/10 bg-white/[0.015] p-6 backdrop-blur-sm">
                <h2 className="text-white text-lg font-semibold">My Applications</h2>
                <p className="text-sm text-gray-400">
                  Track your job application progress
                </p>
              </header>

              <div className="space-y-5">
                {applicationsPending ? (
                  <Card className="border-white/10 bg-white/[0.02]">
                    <CardContent className="p-6 text-gray-400">Loading applications...</CardContent>
                  </Card>
                ) : applications.length === 0 ? (
                  <Card className="border-white/10 bg-white/[0.02]">
                    <CardContent className="p-6 text-gray-400">
                      No applications yet. Apply to roles and they will appear here automatically.
                    </CardContent>
                  </Card>
                ) : (
                  applications.map((app) => (
                    <Card
                      key={app.id}
                      className="role-surface-candidate border-white/10 bg-white/[0.02] hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300"
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between gap-6">
                          <div className="flex-1">
                            <h4 className="text-white mb-1">{app.jobTitle}</h4>
                            <p className="text-sm text-gray-400 mb-3">{app.company}</p>
                            <div className="flex flex-wrap items-center gap-4 text-sm">
                              <Badge className={getStatusBadgeClass(app.statusType)}>
                                {app.statusLabel}
                              </Badge>
                              <span className="text-gray-400 flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {new Date(app.appliedDate).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            className="border-white/20 text-white hover:bg-white/5"
                          >
                            View Details
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Career Path Tab */}
          <TabsContent value="career" className="space-y-8">
            <div className="mx-auto grid w-full max-w-5xl gap-6 md:grid-cols-2">
              <Card className="role-surface-candidate border-white/10 bg-white/[0.02]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#ba9036]" />
                    Career Path Builder
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Your roadmap to success
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {careerPath.map((step, idx) => (
                      <div key={idx} className="flex items-start gap-4">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            step.completed
                              ? 'bg-gradient-to-br from-[#ae8625] to-[#f7ef8a]'
                              : step.recommended
                              ? 'bg-[#ba9036]/20 border-2 border-[#d2ac47]'
                              : 'bg-white/10 border-2 border-white/20'
                          }`}
                        >
                          {step.completed ? (
                            <CheckCircle2 className="w-5 h-5 text-black" />
                          ) : step.recommended ? (
                            <Star className="w-5 h-5 text-[#ba9036]" />
                          ) : (
                            <span className="text-gray-400">{idx + 1}</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-white">{step.role}</h4>
                            {step.current && (
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                                Current
                              </Badge>
                            )}
                            {step.recommended && (
                              <Badge className="bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50">
                                Recommended Next
                              </Badge>
                            )}
                          </div>
                          {step.recommended && (
                            <p className="text-sm text-gray-400 mb-2">
                              Complete 2 training courses to qualify
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="role-surface-candidate border-white/10 bg-white/[0.02]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#ba9036]" />
                    CV Builder
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Generate a professional CV with AI
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-white">Profile Completion</span>
                      <span className="text-[#ba9036]">85%</span>
                    </div>
                    <Progress value={85} className="mb-3" />
                    <p className="text-sm text-gray-400">
                      Add 2 more skills and work experience to reach 100%
                    </p>
                  </div>

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="w-full bg-gold-gradient hover:brightness-110 text-black">
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate CV with AI
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="role-surface-candidate border-white/10 bg-role-candidate-overlay text-white backdrop-blur-xl max-w-2xl">
                      <DialogHeader>
                        <DialogTitle className="text-gold-gradient">
                          AI CV Generator
                        </DialogTitle>
                        <DialogDescription className="text-gray-400">
                          Create a professional CV based on your profile
                        </DialogDescription>
                      </DialogHeader>
                      <CVGeneratorForm />
                    </DialogContent>
                  </Dialog>

                      <Button
                        asChild
                        variant="outline"
                        className="w-full border-white/20 text-white hover:bg-white/5"
                      >
                        <Link to="/candidate/cv">
                          <Download className="w-4 h-4 mr-2" />
                          Download Existing CV
                        </Link>
                      </Button>
                </CardContent>
              </Card>
            </div>

            <section className="mx-auto w-full max-w-5xl rounded-2xl border border-white/10 bg-white/[0.015] p-6 backdrop-blur-sm">
              <header className="mb-6 space-y-1">
                <h2 className="text-white flex items-center gap-2 text-lg font-semibold">
                  <BookOpen className="w-5 h-5 text-[#ba9036]" />
                  Recommended Training Courses
                </h2>
                <p className="text-sm text-gray-400">
                  Upskill with our partner training providers
                </p>
              </header>
              <div className="grid gap-4 md:grid-cols-3">
                {trainingRecommendations.map((training, idx) => (
                  <Card
                    key={idx}
                    className="role-surface-candidate border-white/10 bg-white/[0.02] hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300"
                  >
                    <CardContent className="space-y-3 p-4">
                      <div>
                        <h4 className="text-white mb-1">{training.title}</h4>
                        <p className="text-xs text-gray-400">{training.provider}</p>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">{training.duration}</span>
                        <Badge
                          className={
                            training.price === 'Free'
                              ? 'bg-green-500/20 text-green-400 border-green-500/50'
                              : 'bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50'
                          }
                        >
                          {training.price}
                        </Badge>
                      </div>
                      <Button
                        variant="outline"
                        className="w-full border-white/20 text-white hover:bg-white/5"
                        onClick={() => handleTrainingClick(training)}
                      >
                        Learn More
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          </TabsContent>

          {teamMode && (
            <TabsContent value="team" className="space-y-6">
              <div className="grid gap-6 md:grid-cols-3">
                <Card className="role-surface-candidate border-white/10 bg-white/[0.02]">
                  <CardHeader>
                    <CardTitle className="text-white">Team status</CardTitle>
                    <CardDescription className="text-gray-400">Registration and profile summary.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm text-gray-300">
                    <div className="flex items-center justify-between">
                      <span>Registration</span>
                      <Badge className="bg-green-500/20 border-green-500/40 text-green-200">Team enabled</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Team size</span>
                      <span className="text-white font-semibold">{teamSnapshot?.teamSize ?? listedTeamMembers.length}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Listed members</span>
                      <span className="text-white font-semibold">
                        {teamSnapshot?.teamMemberNames.length ?? listedTeamMembers.length}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="role-surface-candidate border-white/10 bg-white/[0.02] md:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-white">Team members</CardTitle>
                    <CardDescription className="text-gray-400">
                      Keep names, role headlines, and skills updated for better team matching.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {teamMembersQuery.isPending ? (
                      <p className="text-sm text-gray-400">Loading team members…</p>
                    ) : listedTeamMembers.length === 0 ? (
                      <p className="text-sm text-gray-400">No team members recorded yet. Add them from your profile editor.</p>
                    ) : (
                      listedTeamMembers.map((member, index) => (
                        <div key={`${member.name ?? 'member'}-${index}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                          <p className="text-white font-medium">{member.name ?? `Team member ${index + 1}`}</p>
                          <p className="text-sm text-gray-400">{member.title ?? member.headline ?? 'Role headline not set'}</p>
                          {member.skills ? <p className="mt-1 text-xs text-gray-300">Skills: {member.skills}</p> : null}
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card className="role-surface-candidate border-white/10 bg-white/[0.02]">
                <CardHeader>
                  <CardTitle className="text-white">Manage team details</CardTitle>
                  <CardDescription className="text-gray-400">
                    Update the same profile sections used by matching, applications, and employer review.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-3">
                  <Button asChild className="bg-gold-gradient text-black hover:brightness-110">
                    <Link to="/candidate/profile-section/personal-info">Edit team basics</Link>
                  </Button>
                  <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/5">
                    <Link to="/candidate/profile-section/work-experience">Edit team members</Link>
                  </Button>
                  <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/5">
                    <Link to="/candidate/profile-section/skills">Edit team skills</Link>
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {isClient && selectedJob && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setSelectedJob(null)}
          />
          <div className="relative z-10 w-full max-w-3xl max-h-[80vh] overflow-y-auto rounded-2xl border border-white/10 role-surface-candidate bg-role-candidate-overlay text-white backdrop-blur-xl shadow-2xl">
            <div className="p-6 space-y-4 text-sm text-gray-300">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.4em] text-blue-200/80">Job overview</p>
                  <h2 className="text-2xl text-gold-gradient">{selectedJob.title}</h2>
                  <p className="text-gray-300">
                    {resolveSelectedCompany(selectedJob)} • {selectedJob.location} • {selectedJob.type}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  className="text-gray-400 hover:text-white"
                  onClick={() => setSelectedJob(null)}
                >
                  Close
                </Button>
              </div>
              <div className="flex flex-wrap gap-4 text-xs text-gray-400">
                <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                  {selectedJob.matchScore}% Match
                </Badge>
                <span className="flex items-center gap-1">
                  <DollarSign className="h-4 w-4" />
                  {selectedJob.salary}
                </span>
                <span className="flex items-center gap-1">
                  <Navigation className="h-4 w-4" />
                  {(selectedJob as { commute?: string }).commute ?? 'Flexible commute'}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  Posted {selectedJob.posted}
                </span>
              </div>
              <p className="text-base text-gray-200">{selectedJob.description}</p>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <h3 className="text-white mb-2 text-sm uppercase tracking-[0.2em]">Requirements</h3>
                  <ul className="space-y-1">
                    {(selectedJob.requirements ?? []).map((req, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-[#ba9036]">•</span>
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-white mb-2 text-sm uppercase tracking-[0.2em]">Benefits</h3>
                  <ul className="space-y-1">
                    {(selectedJob.benefits ?? []).map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-[#ba9036]">•</span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 pt-4">
                <Button className="bg-gold-gradient text-black hover:brightness-110">
                  Quick Apply
                </Button>
                <Button
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5"
                  onClick={() => setSelectedJob(null)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </main>
  );
}

function CVGeneratorForm() {
  const [cvStyle, setCvStyle] = useState('professional');

  return (
    <ScrollArea className="max-h-[500px] pr-4">
      <form className="space-y-4">
        <div className="space-y-2">
          <Label className="text-white">CV Template Style</Label>
          <Select value={cvStyle} onValueChange={setCvStyle}>
            <SelectTrigger className="bg-white/10 border-white/20 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="role-surface-candidate border-white/10 bg-role-candidate-overlay text-white backdrop-blur-xl">
              <SelectItem value="professional">Professional</SelectItem>
              <SelectItem value="creative">Creative</SelectItem>
              <SelectItem value="modern">Modern</SelectItem>
              <SelectItem value="minimal">Minimal</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-white">Include Additional Information</Label>
          <div className="space-y-2">
            {['Skills & Certifications', 'Work Experience', 'Education', 'References'].map((item) => (
              <label key={item} className="flex items-center gap-2 text-gray-300 cursor-pointer">
                <input type="checkbox" defaultChecked className="rounded" />
                <span className="text-sm">{item}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-white">Personal Statement (Optional)</Label>
          <Textarea
            placeholder="Brief professional summary..."
            className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 min-h-24"
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            type="submit"
            className="flex-1 bg-gold-gradient hover:brightness-110 text-black"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Generate CV
          </Button>
          <Button
            type="button"
            variant="outline"
            className="border-white/20 text-white hover:bg-white/5"
          >
            Preview
          </Button>
        </div>

        <p className="text-xs text-gray-400 text-center">
          Generated CVs will include the Lighthouse Jobs branding
        </p>
      </form>
    </ScrollArea>
  );
}


